/** Fetching laps once, and only when somebody looks at them.
 *
 * A session's timings are small and a session's lap documents are not: one lap
 * carries the full 60 Hz recording, so a stint of fifteen is megabytes and
 * loading them all to draw one would be a bad trade made fifteen times. So the
 * screen reads the timings up front and a lap document only when that lap is
 * selected — which is also why `GET /api/sessions/{id}/laps/{lap}` exists as
 * its own endpoint rather than as a field on the session.
 *
 * Lap documents never change: the service writes one when the lap arrives and
 * replaces it only if the logger re-posts the same lap. That makes them worth
 * caching for the life of the page, which is what the two maps below are, and
 * it is why clicking back to a lap already seen is instant and silent. The
 * cache is bounded — a long stint compared against several drivers would
 * otherwise sit on every lap it ever drew — and a failed request is dropped
 * from it, so a retry is a retry rather than the same error served again.
 *
 * Session summaries are cached too, for a different reason: choosing another
 * driver to compare against needs their session read before their best lap can
 * be named, and the chances are it is a session already read. The one thing
 * that does change is whether a session is public, so `forgetSession` exists
 * for the screen to call after it has changed that.
 */

import { useCallback, useEffect, useState } from 'react';
import { getSession, getSessionAnalysis, getSessionLap } from '@/lib/api';
import type { LapDocument, LapTiming, SessionDetail } from '@/lib/types';
import { bestLap } from '@/components/sessions/derive';
import type { LapAnalysis } from '@/components/sessions/analysis';
import type { Reference } from '@/components/sessions/references';

/** Lap documents are the big ones; this many is a couple of stints. */
const LAP_LIMIT = 24;
const SESSION_LIMIT = 32;

const sessions = new Map<string, Promise<SessionDetail>>();
const laps = new Map<string, Promise<LapDocument>>();
/** The lap documents that have arrived, by the same key: what lets a lap
 *  already seen be drawn on the render that asks for it, rather than one
 *  render later with a loading state in between. Bounded like the cache. */
const arrived = new Map<string, LapDocument>();

function keep(key: string, document: LapDocument): void {
  arrived.delete(key);
  arrived.set(key, document);
  while (arrived.size > LAP_LIMIT) {
    const oldest = arrived.keys().next();
    if (oldest.done) break;
    arrived.delete(oldest.value);
  }
}
const analyses = new Map<string, Promise<LapAnalysis>>();

function remember<T>(store: Map<string, Promise<T>>, key: string, limit: number, load: () => Promise<T>): Promise<T> {
  const known = store.get(key);
  if (known) return known;

  const pending = load().catch((error: unknown) => {
    // A failure is not an answer. Forgetting it is what makes "Try again"
    // mean something.
    store.delete(key);
    throw error;
  });
  store.set(key, pending);

  // Maps iterate in insertion order, so the first key is the oldest.
  while (store.size > limit) {
    const oldest = store.keys().next();
    if (oldest.done || oldest.value === key) break;
    store.delete(oldest.value);
  }
  return pending;
}

/** Drop a session's summary, after its `public` flag has been changed. */
export function forgetSession(id: string): void {
  sessions.delete(id);
  analyses.delete(id);
}

export interface Loaded<T> {
  value: T | null;
  loading: boolean;
  error: unknown;
}

const IDLE: Loaded<never> = { value: null, loading: false, error: null };

/** One session's summary and lap timings. `null` asks for nothing, which is
 *  what the hook is passed while no session is chosen. */
export function useSessionDetail(id: string | null): Loaded<SessionDetail> & { reload: () => void } {
  const [state, setState] = useState<Loaded<SessionDetail>>(IDLE);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!id) {
      setState(IDLE);
      return;
    }
    let live = true;
    setState({ value: null, loading: true, error: null });
    remember(sessions, id, SESSION_LIMIT, () => getSession(id)).then(
      (detail) => {
        if (live) setState({ value: detail, loading: false, error: null });
      },
      (error: unknown) => {
        if (live) setState({ value: null, loading: false, error });
      },
    );
    return () => {
      live = false;
    };
  }, [id, attempt]);

  const reload = useCallback(() => {
    if (id) sessions.delete(id);
    setAttempt((count) => count + 1);
  }, [id]);

  return { ...state, reload };
}

/** One lap document. Nothing is fetched until a lap is actually chosen, and
 *  nothing is fetched for a lap whose recording the service says has gone:
 *  the timing row already answers that, and asking would only collect the
 *  404 that explains it. */
export function useLapDocument(
  sessionId: string | null,
  lap: number | null,
  stored = true,
): Loaded<LapDocument> & { reload: () => void } {
  const [state, setState] = useState<Loaded<LapDocument>>(IDLE);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!sessionId || lap === null || !stored) {
      setState(IDLE);
      return;
    }
    let live = true;
    const key = `${sessionId}:${lap}`;
    const known = arrived.get(key);
    setState(known ? { value: known, loading: false, error: null } : { value: null, loading: true, error: null });
    remember(laps, key, LAP_LIMIT, () => getSessionLap(sessionId, lap)).then(
      (document) => {
        keep(key, document);
        if (live) setState({ value: document, loading: false, error: null });
      },
      (error: unknown) => {
        if (live) setState({ value: null, loading: false, error });
      },
    );
    return () => {
      live = false;
    };
  }, [sessionId, lap, stored, attempt]);

  const reload = useCallback(() => {
    if (sessionId && lap !== null) {
      laps.delete(`${sessionId}:${lap}`);
      arrived.delete(`${sessionId}:${lap}`);
    }
    setAttempt((count) => count + 1);
  }, [sessionId, lap]);

  return { ...state, reload };
}

/**
 * Several lap documents of one session at once, for the cards that read a
 * stint rather than a lap. Nothing is fetched until `wanted` is true: these
 * are megabytes each, so the screen asks only when the driver does. The
 * documents share the one-at-a-time cache, so a lap already drawn costs
 * nothing again, and a failure anywhere is reported as one, with the laps
 * that did arrive kept.
 */
export function useLapDocuments(
  sessionId: string | null,
  lapNumbers: readonly number[],
  wanted: boolean,
): Loaded<Map<number, LapDocument>> & { reload: () => void } {
  const [state, setState] = useState<Loaded<Map<number, LapDocument>>>(IDLE);
  const [attempt, setAttempt] = useState(0);
  const key = lapNumbers.join(',');

  useEffect(() => {
    if (!sessionId || !wanted || lapNumbers.length === 0) {
      setState(IDLE);
      return;
    }
    let live = true;
    setState({ value: null, loading: true, error: null });
    Promise.allSettled(
      lapNumbers.map((lap) =>
        remember(laps, `${sessionId}:${lap}`, LAP_LIMIT, () => getSessionLap(sessionId, lap)).then((document) => {
          keep(`${sessionId}:${lap}`, document);
          return [lap, document] as const;
        }),
      ),
    ).then((settled) => {
      if (!live) return;
      const value = new Map<number, LapDocument>();
      let error: unknown = null;
      for (const result of settled) {
        if (result.status === 'fulfilled') value.set(result.value[0], result.value[1]);
        else error ??= result.reason;
      }
      setState({ value, loading: false, error });
    });
    return () => {
      live = false;
    };
    // The numbers are compared as one string so a fresh array of the same
    // laps does not fetch again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, key, wanted, attempt]);

  const reload = useCallback(() => {
    if (sessionId) for (const lap of lapNumbers) laps.delete(`${sessionId}:${lap}`);
    setAttempt((count) => count + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, key]);

  return { ...state, reload };
}

/** A session's analysis, fetched only when the session says it has one — a
 *  session without is complete, and asking would only collect a 404. */
export function useSessionAnalysis(
  sessionId: string | null,
  present: boolean,
): Loaded<LapAnalysis> & { reload: () => void } {
  const [state, setState] = useState<Loaded<LapAnalysis>>(IDLE);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!sessionId || !present) {
      setState(IDLE);
      return;
    }
    let live = true;
    setState({ value: null, loading: true, error: null });
    remember(analyses, sessionId, SESSION_LIMIT, () => getSessionAnalysis(sessionId)).then(
      (document) => {
        if (live) setState({ value: document, loading: false, error: null });
      },
      (error: unknown) => {
        if (live) setState({ value: null, loading: false, error });
      },
    );
    return () => {
      live = false;
    };
  }, [sessionId, present, attempt]);

  const reload = useCallback(() => {
    if (sessionId) analyses.delete(sessionId);
    setAttempt((count) => count + 1);
  }, [sessionId]);

  return { ...state, reload };
}

export interface ReferenceLap {
  /** The reference's own best lap, once the session holding it has been read. */
  lap: LapTiming | null;
  document: LapDocument | null;
  loading: boolean;
  error: unknown;
  reload: () => void;
}

/**
 * The lap a comparison is drawn against.
 *
 * Your own baseline is the best lap of the session already on screen, so it
 * costs one request for the document. Another driver costs two — their public
 * session names the best lap, then the lap itself — because the "compare with"
 * listing gives a time without saying which lap set it, and asking every
 * driver's session up front would fetch three sessions to draw none of them.
 */
export function useReferenceLap(
  reference: Reference | null,
  ownSessionId: string | null,
  ownLaps: readonly LapTiming[],
): ReferenceLap {
  const otherSessionId = reference?.driver?.session_id ?? null;
  const other = useSessionDetail(otherSessionId);

  const sessionId = otherSessionId ?? (reference ? ownSessionId : null);
  const source = otherSessionId ? (other.value?.laps ?? []) : ownLaps;
  const best = bestLap(source);
  const document = useLapDocument(sessionId, best?.lap ?? null, best?.stored !== false);

  // The two `reload`s are stable in their own hooks, so this one is too — it
  // is handed to a retry button and must not change identity every render.
  const reloadOther = other.reload;
  const reloadDocument = document.reload;
  const reload = useCallback(() => {
    reloadOther();
    reloadDocument();
  }, [reloadOther, reloadDocument]);

  return {
    lap: best,
    document: document.value,
    loading: other.loading || document.loading,
    error: other.error ?? document.error,
    reload,
  };
}
